import React, { useState, useEffect } from 'react';
import ProTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ORDER_TYPE, MAX_VOCA_COUNT } from '../../data/common';
import FoldingGrayIc from '../../assets/images/ic_folding_arrow.svg';
import FoldingBlueIc from '../../assets/images/ic_folding_arrow_blue.svg';
import VocaOrdering from './VocaOrdering';
import VocaTypeFiltering from './VocaTypeFiltering';
import VocaRow from './VocaRow';
import AlertModal from '../common/AlertModal';

function VocabularyChoice({
  vocabularyList,
  setVocabularyList,
  isFolding,
  setIsFolding,
  isExchange = undefined,
  orderingCheck,
  setOrderingCheck,
}) {
  const navigate = useNavigate();
  const [countAlert, setCountAlert] = useState(null);
  const [typeFilters, setTypeFilters] = useState([]);
  const [paintVocabularyList, setPaintVocabularyList] = useState([]);

  const checkedLength =
    vocabularyList?.filter(voca => voca.checked)?.length ?? 0;

  // 교재순서/알파벳순서/랜덤순서 필터링
  useEffect(() => {
    if (orderingCheck === ORDER_TYPE.BOOK) {
      // 먼저는 un_idx가 빠른 순, un_idx가 같으면 vc_order가 빠른 순
      setVocabularyList(
        vocabularyList
          .map(voca => ({
            ...voca,
          }))
          .sort((a, b) => {
            if (a.un_idx === b.un_idx) {
              return a.vc_order - b.vc_order;
            }
            return a.un_idx - b.un_idx;
          }),
      );
    } else if (orderingCheck === ORDER_TYPE.ALPHABET) {
      setVocabularyList(
        vocabularyList
          ?.map(voca => ({
            ...voca,
          }))
          ?.sort((a, b) =>
            a.word.localeCompare(b.word, 'en', { ignorePunctuation: true }),
          ),
      );
    } else if (orderingCheck === ORDER_TYPE.RANDOM) {
      setVocabularyList(
        vocabularyList
          .map(voca => ({
            ...voca,
          }))
          .sort(() => Math.random() - 0.5),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingCheck]);

  // 문제유형 필터 초기화
  useEffect(() => {
    const totalTypes = [
      { type: 'A', checked: false, disabled: false },
      { type: 'B', checked: false, disabled: false },
      { type: 'C', checked: false, disabled: false },
      { type: 'D', checked: false, disabled: false },
      { type: 'E', checked: false, disabled: false },
      { type: 'F', checked: false, disabled: false },
      { type: 'G', checked: false, disabled: false },
      { type: 'H', checked: false, disabled: false },
      { type: 'I', checked: false, disabled: false },
      { type: 'J', checked: false, disabled: false },
      { type: 'K', checked: false, disabled: false },
      { type: 'L', checked: false, disabled: false },
      { type: 'M', checked: false, disabled: false },
    ];
    // disabled처리
    const result = totalTypes.map(type => {
      const isExist = vocabularyList.find(voca =>
        voca.problemTypes?.find(t => t.type === type.type),
      );
      return isExist
        ? { ...type, disabled: false }
        : { ...type, disabled: true };
    });
    setTypeFilters(result);
  }, [vocabularyList]);

  // filter에 따라 보여질 리스트 필터링(vocabulayList에서 제하지는 않고 단지 보여질지 안보여질지만 결정)
  useEffect(() => {
    const checkedFilters = typeFilters.filter(f => f.checked && !f.disabled);
    if (checkedFilters?.length === 0) {
      setPaintVocabularyList(vocabularyList);
      return;
    } else {
      const result = vocabularyList.filter(voca => {
        const isChecked = checkedFilters.find(f => f.type === voca.chosenType);
        return isChecked;
      });
      setPaintVocabularyList(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilters]);

  // 선택된 타입 추가
  const onAddSelectVoca = (checked, vocaIdx) => {
    if (checked && checkedLength === MAX_VOCA_COUNT) {
      setCountAlert(`문제 출제는 최대 ${MAX_VOCA_COUNT}개까지 가능합니다.`);
      return;
    }
    if (checked) {
      setVocabularyList(
        vocabularyList.map(voca =>
          voca.idx === vocaIdx
            ? { ...voca, checked: true, chosenType: 'A' }
            : voca,
        ),
      );
    } else {
      setVocabularyList(
        vocabularyList.map(voca =>
          voca.idx === vocaIdx
            ? { ...voca, checked: false, chosenType: null }
            : voca,
        ),
      );
    }
  };

  // 교재이동시 교재순/알파뱃순/랜덤순 초기화
  useEffect(() => {
    if (isExchange) {
      setOrderingCheck(ORDER_TYPE.BOOK);
      navigate('.', { replace: true });
    }
  }, [isExchange, navigate, setOrderingCheck]);

  return (
    <div
      className="flex flex-col bg-[#FFF] h-full min-w-[400px] border-r-[3px] border-[#969ba4] transition-all overflow-hidden"
      style={{
        minWidth: isFolding ? '41px' : '400px',
        width: isFolding ? '41px' : '400px',
      }}
    >
      <div
        className="max-h-[54px] h-[54px] px-[8px] py-[10px] flex items-center justify-between border-b border-[#E7E7E7]"
        style={isFolding ? { border: 'none' } : {}}
      >
        <div
          className="flex justify-start items-center gap-3"
          style={{ display: isFolding ? 'none' : 'flex' }}
        >
          <div className="flex justify-between items-center gap-1">
            <div className="h-[34px] font-bold text-[14px] flex items-center overflow-hidden whitespace-nowrap">
              선택된 단어 수
            </div>
            <div className="h-[34px] text-[24px] font-bold flex items-center">
              {checkedLength}
            </div>
          </div>
          <div className="flex items-center">/</div>
          <div className="flex justify-between items-center gap-1">
            <div className="h-[34px text-[14px] flex items-center">
              전체 단어 수
            </div>
            <div className="h-[34px] text-[14px] flex items-center">
              {vocabularyList?.filter(voca => voca.shouldShow)?.length}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            setIsFolding(prev => {
              return { ...prev, voca: !prev.voca };
            })
          }
        >
          <img
            src={isFolding ? FoldingBlueIc : FoldingGrayIc}
            alt="folding"
            style={{
              minWidth: '22px',
              minHeight: '22px',
            }}
          />
        </button>
      </div>
      <div
        className="flex justify-center text-[14px] font-[700] mt-[-5px]"
        style={{ display: isFolding ? 'flex' : 'none' }}
      >
        단어
      </div>
      {!isFolding && (
        <>
          <VocaOrdering
            orderingCheck={orderingCheck}
            setOrderingCheck={setOrderingCheck}
            vocabularyList={vocabularyList}
            setVocabularyList={setVocabularyList}
          />
          <VocaTypeFiltering
            typeFilters={typeFilters}
            setTypeFilters={setTypeFilters}
          />
        </>
      )}
      <div
        className="grow min-h-[0px] w-full overflow-y-auto overflow-x-hidden"
        style={{ display: isFolding ? 'none' : 'block' }}
      >
        {paintVocabularyList?.length >= 1 &&
          paintVocabularyList
            ?.filter(voca => voca.shouldShow)
            ?.map((voca, idx) => {
              return (
                <VocaRow
                  key={`${voca.idx}-${String(idx)}`}
                  voca={voca}
                  vocabularyList={vocabularyList}
                  setVocabularyList={setVocabularyList}
                  onAddSelectVoca={onAddSelectVoca}
                  checkedLength={checkedLength}
                />
              );
            })}
      </div>
      <AlertModal
        open={countAlert !== null ? true : false}
        setOpen={setCountAlert}
        content={countAlert}
        btnText="확인"
        onConfirm={() => setCountAlert(null)}
      />
    </div>
  );
}

VocabularyChoice.propTypes = {
  vocabularyList: ProTypes.array.isRequired,
  setVocabularyList: ProTypes.func.isRequired,
  isFolding: ProTypes.bool.isRequired,
  setIsFolding: ProTypes.func.isRequired,
  isExchange: ProTypes.bool,
  orderingCheck: ProTypes.number.isRequired,
  setOrderingCheck: ProTypes.func.isRequired,
};

export default React.memo(VocabularyChoice);
